import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Copy } from 'lucide-react';
import { getToken } from '../lib/api';
import { Dialog } from '../components/Dialog';
import { useToast } from '../components/Toast';

const SCOPES = ['Reports', 'PhoneSystemAdmin', 'Users', 'Trunks', 'Backups', 'CallControl'];

export default function ApiKeys() {
  const qc = useQueryClient();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<any>({ scopes: ['Reports'] });
  const [shown, setShown] = useState<{ name: string; api_key: string } | null>(null);

  // Note: there's no list endpoint yet — the create endpoint is /api/actions/GenerateApiKey
  // and delete is /api/actions/DeleteApiKey. List would require a small route addition.
  const { data: list = [] } = useQuery<any[]>({
    queryKey: ['api-keys'],
    queryFn: async () => {
      // Placeholder — would query stratus_ext.api_keys via a list endpoint
      const r = await fetch('/api/parameters?_listKeys=1', { headers: { Authorization: `Bearer ${getToken()}` } });
      return r.ok ? [] : [];
    },
  });

  const create = useMutation({
    mutationFn: async () => {
      const r = await fetch('/api/actions/GenerateApiKey', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify(form),
      });
      if (!r.ok) throw new Error(await r.text());
      return r.json();
    },
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['api-keys'] });
      setOpen(false);
      setShown({ name: form.name, api_key: r.api_key });
    },
    onError: (e: any) => toast('error', e.message),
  });

  function copy(text: string) {
    navigator.clipboard.writeText(text);
    toast('success', 'Copied');
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">API Keys</h1>
        <button onClick={() => { setForm({ scopes: ['Reports'] }); setOpen(true); }} className="btn-primary inline-flex items-center gap-2"><Plus size={14} /> Generate</button>
      </div>
      <p className="text-sm text-textSecondary">Top-level API keys for scripts and integrations. Distinct from <a href="/service-principals" className="text-primary hover:underline">Service Principals</a> (which issue 60-second OAuth tokens) and <a href="/" className="text-primary hover:underline">Personal Access Tokens</a> (under your profile).</p>

      <div className="card p-0 overflow-hidden">
        <table className="table">
          <thead><tr><th>Name</th><th>Prefix</th><th>Scopes</th><th>Last used</th><th>Expires</th><th></th></tr></thead>
          <tbody>
            {list.map((k: any) => (
              <tr key={k.key_uuid} className="hover:bg-elevated">
                <td>{k.name}</td>
                <td className="font-mono text-xs">{k.key_prefix}</td>
                <td className="text-xs">{(k.scopes || []).join(', ')}</td>
                <td className="text-xs">{k.last_used_at ? new Date(k.last_used_at).toLocaleString() : '—'}</td>
                <td className="text-xs">{k.expires_at ? new Date(k.expires_at).toLocaleString() : 'never'}</td>
                <td>
                  <button className="btn-ghost p-1 text-danger" onClick={() => fetch('/api/actions/DeleteApiKey', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
                    body: JSON.stringify({ key_uuid: k.key_uuid }),
                  }).then(() => qc.invalidateQueries({ queryKey: ['api-keys'] }))}>
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!list.length && <div className="p-6 text-center text-textSecondary">No API keys yet. Generate one to get started.</div>}
      </div>

      <Dialog open={open} onClose={() => setOpen(false)} title="Generate API Key" size="md"
        footer={<><button onClick={() => setOpen(false)} className="btn-ghost">Cancel</button><button onClick={() => create.mutate()} className="btn-primary">Generate</button></>}>
        <div className="space-y-3">
          <div>
            <label className="block text-sm text-textSecondary mb-1">Name *</label>
            <input className="input" value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="my-zapier-integration" />
          </div>
          <div>
            <label className="block text-sm text-textSecondary mb-1">Scopes</label>
            <div className="space-y-1">
              {SCOPES.map((s) => (
                <label key={s} className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={(form.scopes || []).includes(s)} onChange={(e) => {
                    const cur = form.scopes || [];
                    setForm({ ...form, scopes: e.target.checked ? [...cur, s] : cur.filter((x: string) => x !== s) });
                  }} />
                  {s}
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm text-textSecondary mb-1">Expires (optional)</label>
            <input className="input" type="datetime-local" value={form.expires_at || ''} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} />
          </div>
        </div>
      </Dialog>

      <Dialog open={!!shown} onClose={() => setShown(null)} title={`API Key created: ${shown?.name}`} size="md"
        footer={<button onClick={() => setShown(null)} className="btn-primary">I've saved it</button>}>
        <div className="space-y-3">
          <p className="text-warning text-sm">⚠ Copy this key now. It cannot be retrieved later.</p>
          <div className="flex gap-2">
            <input className="input font-mono" readOnly value={shown?.api_key || ''} />
            <button onClick={() => copy(shown?.api_key || '')} className="btn-ghost"><Copy size={14} /></button>
          </div>
          <p className="text-xs text-textSecondary">Use as <code className="bg-elevated px-1 font-mono">Authorization: Bearer &lt;key&gt;</code> in API requests.</p>
        </div>
      </Dialog>
    </div>
  );
}
